/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  RemoteSessionClient,
  RemoteSessionClient$inboundSchema,
} from "./remotesessionclient.js";
import {
  RemoteSessionIssuer,
  RemoteSessionIssuer$inboundSchema,
} from "./remotesessionissuer.js";
import {
  ServerIdentityRegistrationFailure,
  ServerIdentityRegistrationFailure$inboundSchema,
} from "./serveridentityregistrationfailure.js";

/**
 * The client ownership tier.
 */
export const ClientTier = {
  ProjectSpecific: "project-specific",
  OrganizationLevel: "organization-level",
} as const;
/**
 * The client ownership tier.
 */
export type ClientTier = ClosedEnum<typeof ClientTier>;

/**
 * The provider ownership tier.
 */
export const ProviderTier = {
  ProjectSpecific: "project-specific",
  OrganizationLevel: "organization-level",
  PlatformLevel: "platform-level",
} as const;
/**
 * The provider ownership tier.
 */
export type ProviderTier = ClosedEnum<typeof ProviderTier>;

/**
 * How the client was obtained. Present on success and registration failure.
 */
export const CommitServerIdentityConfigurationResultRegistrationMethod = {
  Cimd: "cimd",
  Dcr: "dcr",
  Manual: "manual",
  Existing: "existing",
} as const;
/**
 * How the client was obtained. Present on success and registration failure.
 */
export type CommitServerIdentityConfigurationResultRegistrationMethod =
  ClosedEnum<typeof CommitServerIdentityConfigurationResultRegistrationMethod>;

/**
 * Successful commit status. Present only after local commit.
 */
export const CommitServerIdentityConfigurationResultStatus = {
  Registered: "registered",
  Linked: "linked",
} as const;
/**
 * Successful commit status. Present only after local commit.
 */
export type CommitServerIdentityConfigurationResultStatus = ClosedEnum<
  typeof CommitServerIdentityConfigurationResultStatus
>;

/**
 * The outcome of committing a Remote MCP server user-identity plan. registered and linked are successful local commits. A registration failure is returned in failure. manual_setup_required is a preparation signal, not a registration outcome, and means auto mode found neither usable CIMD nor DCR support; no local state changed.
 */
export type CommitServerIdentityConfigurationResult = {
  /**
   * A remote_session_client record. client_secret_encrypted is never returned.
   */
  client?: RemoteSessionClient | undefined;
  /**
   * Organization-relative dashboard path to the client detail surface.
   */
  clientPath?: string | undefined;
  /**
   * The client ownership tier.
   */
  clientTier?: ClientTier | undefined;
  /**
   * A completed automatic registration failure using the bounded OAuth registration taxonomy.
   */
  failure?: ServerIdentityRegistrationFailure | undefined;
  /**
   * True only when auto mode found neither usable CIMD nor DCR support. This is not a registration failure and no local state was changed.
   */
  manualSetupRequired: boolean;
  /**
   * A remote_session_issuer record — upstream Authorization Server identity that Speakeasy speaks OAuth to.
   */
  provider?: RemoteSessionIssuer | undefined;
  /**
   * Organization-relative dashboard path to the Remote Identity Provider detail surface.
   */
  providerPath?: string | undefined;
  /**
   * The provider ownership tier.
   */
  providerTier?: ProviderTier | undefined;
  /**
   * How the client was obtained. Present on success and registration failure.
   */
  registrationMethod?:
    | CommitServerIdentityConfigurationResultRegistrationMethod
    | undefined;
  /**
   * Successful commit status. Present only after local commit.
   */
  status?: CommitServerIdentityConfigurationResultStatus | undefined;
};

/** @internal */
export const ClientTier$inboundSchema: z.ZodMiniEnum<typeof ClientTier> = z
  .enum(ClientTier);

/** @internal */
export const ProviderTier$inboundSchema: z.ZodMiniEnum<typeof ProviderTier> = z
  .enum(ProviderTier);

/** @internal */
export const CommitServerIdentityConfigurationResultRegistrationMethod$inboundSchema:
  z.ZodMiniEnum<
    typeof CommitServerIdentityConfigurationResultRegistrationMethod
  > = z.enum(CommitServerIdentityConfigurationResultRegistrationMethod);

/** @internal */
export const CommitServerIdentityConfigurationResultStatus$inboundSchema:
  z.ZodMiniEnum<typeof CommitServerIdentityConfigurationResultStatus> = z.enum(
    CommitServerIdentityConfigurationResultStatus,
  );

/** @internal */
export const CommitServerIdentityConfigurationResult$inboundSchema:
  z.ZodMiniType<CommitServerIdentityConfigurationResult, unknown> = z.pipe(
    z.object({
      client: z.optional(RemoteSessionClient$inboundSchema),
      client_path: z.optional(z.string()),
      client_tier: z.optional(ClientTier$inboundSchema),
      failure: z.optional(ServerIdentityRegistrationFailure$inboundSchema),
      manual_setup_required: z.boolean(),
      provider: z.optional(RemoteSessionIssuer$inboundSchema),
      provider_path: z.optional(z.string()),
      provider_tier: z.optional(ProviderTier$inboundSchema),
      registration_method: z.optional(
        CommitServerIdentityConfigurationResultRegistrationMethod$inboundSchema,
      ),
      status: z.optional(
        CommitServerIdentityConfigurationResultStatus$inboundSchema,
      ),
    }),
    z.transform((v) => {
      return remap$(v, {
        "client_path": "clientPath",
        "client_tier": "clientTier",
        "manual_setup_required": "manualSetupRequired",
        "provider_path": "providerPath",
        "provider_tier": "providerTier",
        "registration_method": "registrationMethod",
      });
    }),
  );

export function commitServerIdentityConfigurationResultFromJSON(
  jsonString: string,
): SafeParseResult<
  CommitServerIdentityConfigurationResult,
  SDKValidationError
> {
  return safeParse(
    jsonString,
    (x) =>
      CommitServerIdentityConfigurationResult$inboundSchema.parse(
        JSON.parse(x),
      ),
    `Failed to parse 'CommitServerIdentityConfigurationResult' from JSON`,
  );
}
